import React, { useContext, useEffect } from 'react';
import FormField from '../../../form/FormField';
import { FieldArray, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import AlertStatusSetupContext, { ScreeningSetupDataType } from './AlertStatusSetupContext';
import { SalvFieldHeader, SalvRadioToggleButton, SalvRadioToggleGroup } from '@salvapp/ui-components';

const validationSchema: Yup.Schema<ScreeningSetupDataType> = Yup.object()
  .shape({
    alertSubtype: Yup.object().shape({
      isSanctions: Yup.boolean().required(),
      isPep: Yup.boolean().required(),
      isAdverseMedia: Yup.boolean().required(),
      isAdverseMediaEntities: Yup.boolean().required(),
      customLists: Yup.array()
        .of(
          Yup.object().shape({
            id: Yup.string().required(),
            name: Yup.string().required(),
            isEnabled: Yup.boolean().required(),
          })
        )
        .required(),
    }),
    isGoodlistable: Yup.boolean().required(),
  })
  .test({
    test: (values) => {
      if (
        values.alertSubtype.isPep ||
        values.alertSubtype.isSanctions ||
        values.alertSubtype.isAdverseMedia ||
        values.alertSubtype.isAdverseMediaEntities ||
        values.alertSubtype.customLists?.some((customList: { isEnabled: boolean }) => customList.isEnabled)
      ) {
        return true;
      } else {
        return new Yup.ValidationError('At least one alert type has to be chosen', null, 'alertSubtype');
      }
    },
  });

const ScreeningSetup = () => {
  const { formRef, screeningSetup, generalSetup, progressStepper } = useContext(AlertStatusSetupContext)!;

  const { data, setData, setValid, isSubmissionTouched, setSubmitted } = screeningSetup;
  const { data: generalSetupData } = generalSetup;
  const { setScreeningSetupStep } = progressStepper;

  const submit = (values: ScreeningSetupDataType) => {
    setData(values);
    setSubmitted(true);
    setScreeningSetupStep({ type: 'filled' });
  };

  return (
    <div>
      <h2 className={'title-m m-b-12'}>Screening setup</h2>
      <Formik initialValues={data} validationSchema={validationSchema} onSubmit={submit} innerRef={formRef}>
        {({ setFieldValue, values, errors, isValid }: FormikProps<ScreeningSetupDataType>) => {
          useEffect(() => {
            setValid(isValid);
          }, [isValid]);

          return (
            <>
              <div className={'m-b-24'}>
                <SalvFieldHeader
                  label={'Alert type'}
                  description={'Choose alerts based on the alert type for which you want to enable this status.'}
                />
                <div className={`list-box ${isSubmissionTouched && errors.alertSubtype ? 'invalid' : ''}`}>
                  <div>PEP / RCA</div>
                  <FormField
                    id={'isPep'}
                    name={'isPep'}
                    type='checkbox'
                    onFieldChange={(e) => {
                      setFieldValue('alertSubtype.isPep', e.target.checked);
                    }}
                    checked={values.alertSubtype.isPep}
                    isInvalid={isSubmissionTouched && !!errors.alertSubtype}
                  />
                </div>
                <div className={`list-box ${isSubmissionTouched && errors.alertSubtype ? 'invalid' : ''}`}>
                  <div>Sanctions</div>
                  <FormField
                    id={'isSanctions'}
                    name={'isSanctions'}
                    type='checkbox'
                    onFieldChange={(e) => {
                      setFieldValue('alertSubtype.isSanctions', e.target.checked);
                    }}
                    checked={values.alertSubtype.isSanctions}
                    isInvalid={isSubmissionTouched && !!errors.alertSubtype}
                  />
                </div>
                <div className={`list-box ${isSubmissionTouched && errors.alertSubtype ? 'invalid' : ''}`}>
                  <div>Adverse media</div>
                  <FormField
                    id={'isAdverseMedia'}
                    name={'isAdverseMedia'}
                    type='checkbox'
                    onFieldChange={(e) => {
                      setFieldValue('alertSubtype.isAdverseMedia', e.target.checked);
                    }}
                    checked={values.alertSubtype.isAdverseMedia}
                    isInvalid={isSubmissionTouched && !!errors.alertSubtype}
                  />
                </div>
                <div className={`list-box ${isSubmissionTouched && errors.alertSubtype ? 'invalid' : ''}`}>
                  <div>Adverse media entities</div>
                  <FormField
                    id={'isAdverseMediaEntities'}
                    name={'isAdverseMediaEntities'}
                    type='checkbox'
                    onFieldChange={(e) => {
                      setFieldValue('alertSubtype.isAdverseMediaEntities', e.target.checked);
                    }}
                    checked={values.alertSubtype.isAdverseMediaEntities}
                    isInvalid={isSubmissionTouched && !!errors.alertSubtype}
                  />
                </div>
                <FieldArray name={'customLists'}>
                  {() => {
                    return values.alertSubtype.customLists.map((customList, index) => {
                      return (
                        <div
                          key={customList.id}
                          className={`list-box
                          ${isSubmissionTouched && errors.alertSubtype ? 'invalid' : ''}`}
                        >
                          <div>{customList.name}</div>
                          <FormField
                            id={customList.id}
                            name={customList.name}
                            type='checkbox'
                            onFieldChange={(e) => {
                              setFieldValue(`alertSubtype.customLists.${index}.isEnabled`, e.target.checked);
                            }}
                            checked={values.alertSubtype.customLists[index].isEnabled}
                            isInvalid={isSubmissionTouched && !!errors.alertSubtype}
                          />
                        </div>
                      );
                    });
                  }}
                </FieldArray>
              </div>
              <SalvRadioToggleGroup
                label={'Goodlistable'}
                description={
                  <>
                    <div>
                      {`Select whether you would like for this status to trigger goodlisting logic or not.
                        If yes, then selecting this status for an alert will result in the related entity being goodlisted.`}
                    </div>
                    {!generalSetupData.isFinal && (
                      <div className={'m-t-4'}>{`Goodlisting can only be applied to a "Final status"`}</div>
                    )}
                  </>
                }
                value={values.isGoodlistable}
                onChange={(value) => {
                  setFieldValue('isGoodlistable', value === 'true');
                }}
                disabled={!generalSetupData.isFinal}
                className={'m-b-24'}
              >
                <SalvRadioToggleButton value={false} label={'No'} />
                <SalvRadioToggleButton value={true} label={'Yes'} />
              </SalvRadioToggleGroup>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default ScreeningSetup;
